/**
 * src/components/games/launchers/FreeDoomLauncher.tsx
 *
 * Single-player FreeDoom voice activity. The actual game runs inside
 * `public/freedoom/index.html` (vendored js-dos@8.4.1 UMD bundle + a
 * FreeDoom IWAD zipped into a `.jsdos` bundle) with a structured
 * postMessage bridge at `public/freedoom/launcher-bridge.js`.
 *
 * Per-map leaderboard pipeline (v1.2)
 * ----------------------------------
 * The launcher is non-competitive by design — no per-frame score,
 * no relative ranking, just per-map fastest completion time. The
 * React wrapper owns a custom toolbar (Submit Time / Next Map /
 * Restart Timer + per-map timer display) that drives three envelopes
 * on the bridge. The bridge then emits one outbound envelope
 * (`psydechat:map_complete` v1) per Submit click, which the host listener
 * routes to `public.upsert_speedrun(p_game_id, p_map_id, p_time_ms)`
 * from `supabase/migrations/20260901000000_create_games_leaderboards.sql`.
 *
 * Map tracking is manual because js-dos has no first-class DOOM
 * map-end event. Auto-detection (par-time heuristic, OCR of the
 * intermission screen) is brittle and a v1.3 follow-up — for now the
 * player clicks "Next Map" when they finish. The toolbar runs its own
 * 250ms local ticker so the display shows centisecond precision; the
 * bridge tracks the authoritative per-map time independently and the
 * `levelTimeMs` heartbeat field carries the session total while
 * `mapTimeMs` carries the per-map total.
 *
 * Same-origin postMessage policy
 *   `sameOrigin: true` opts the launcher into validating against
 *   `window.location.origin` since `/freedoom/index.html` is vendored
 *   out of `/public/`.
 *
 * Asset vendoring is a deployment step — see
 * `public/freedoom/THIRD_PARTY_ASSETS.md` for the asset inventory and
 * `scripts/vendor-freedoom.mjs` for the build pipeline.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Skull, AlertTriangle, XCircle } from 'lucide-react';
import {
  createIframeGame,
  type ToolbarEmitEvent,
  type ToolbarState,
} from './IframeGameActivity';

/**
 * Pure formatter MM:SS.cc (centiseconds). Mirrors the bridge's
 * formatTimeMs and the host listener's formatTimeMs so the toast text,
 * toolbar display, and bridge status all use the same shape.
 */
function fmtTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return (
    String(min).padStart(2, '0') +
    ':' +
    String(sec).padStart(2, '0') +
    '.' +
    String(cs).padStart(2, '0')
  );
}

/**
 * Cycle a MAPxx identifier (MAP01 → MAP02 → ... → MAP32 → MAP01).
 * Returns input unchanged on unrecognized shape so a hostile React
 * caller passing junk gets clamped by the bridge's regex later.
 */
function nextMapId(mapId: string): string {
  const m = /^MAP(\d{1,2})$/.exec(mapId || '');
  if (!m) return 'MAP01';
  const num = parseInt(m[1], 10);
  if (!Number.isFinite(num)) return 'MAP01';
  return 'MAP' + String((num % 32) + 1).padStart(2, '0');
}

/**
 * FreeDoomToolbar — React wrapper component that owns the per-map
 * timer + Submit/Next/Restart controls. Receives an `api` from the
 * Activity carrying both `emit` (post typed `ToolbarEmitEvent`
 * messages into the iframe) and `state` (the mirrored version of
 * the bridge's `psydechat:ready` envelope — `engineReady`,
 * `mountedBundle`, `currentMap`, etc.).
 *
 * Empty-state: when the bridge is reachable but no .jsdos bundle
 * is vendored yet (`engineReady && mountedBundle === null`), the
 * Submit / Next / Restart buttons are disabled and an amber
 * notice pill appears next to the MAPxx chip with the exact
 * recovery command (`npm run vendor:freedoom`). When the bridge
 * hasn't reported yet (`!engineReady`), the toolbar still
 * renders (so the activity chrome isn't blank) but every
 * actionable button stays disabled and the timer chip carries
 * a "BOOTING…" caption so the user understands the wait state.
 */
function FreeDoomToolbar({
  api,
}: {
  api: { emit: (event: ToolbarEmitEvent) => void; state: ToolbarState };
}) {
  const { state } = api;
  // `engineReady` baseline {false} lasts ~50ms between mount and the
  // bridge's first `psydechat:ready` postMessage. We don't render a
  // separate `booting` pill for that window because a flickering
  // status chip is harder to read than a quiet toolbar — the bridge's
  // own JS splash already says "PSYDECHAT LAUNCHER · WAITING FOR
  // PARENT" until ready arrives.
  // Local ticker — drives the per-map elapsed display. 250ms gives
  // centisecond precision without burning CPU. The bridge tracks the
  // authoritative per-map time independently (mapStartedAtMs reset
  // on `psydechat:advance_map` / `psydechat:reset_timer`); the React
  // display source-of-truth is the local ticker so the user sees a
  // smooth counter even if the heartbeat round-trip is delayed.
  const [now, setNow] = useState<number>(() => Date.now());
  const [currentMap, setCurrentMap] = useState<string>('MAP01');
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, []);

  const elapsed = now - startTime;

  // Submit Time — fires psydechat:submit_time to the bridge, which
  // computes timeMs = Date.now() - mapStartedAtMs and emits
  // psydechat:map_complete upstream. The local `submitting` flag debounces
  // a chatty React double-fire so the bridge only ever emits once
  // per click. 3-second debounce matches the toast display window.
  // Bundle-availability gate derived from the bridge state's
  // `mountedBundle`. `null` means the bridge couldn't mount any
  // .jsdos (vendor step pending). The toolbar's actionable
  // controls stay disabled in this state because Submit / Next /
  // Restart would otherwise post envelopes into a bridge whose
  // per-map timer is paused / never started.
  // Red-pill condition: EITHER the 4s safety-timeout fired with no
  // `psydechat:ready` (bridge never reported — CDN/CSP/404 on js-dos.js)
  // OR the bridge posted `psydechat:error` (engine script load failure,
  // runtime crash, remount failure). The bridge's own message is
  // surfaced when available so the user sees the REAL cause instead of
  // a hardcoded guess.
  const engineFailed = state.engineError !== null;
  const bundleReady =
    state.engineReady === true &&
    state.mountedBundle !== null &&
    !engineFailed;

  const submit = useCallback(() => {
    if (submitting || !bundleReady) return;
    setSubmitting(true);
    api.emit({ type: 'psydechat:submit_time', mapId: currentMap });
    window.setTimeout(() => setSubmitting(false), 3000);
  }, [api, bundleReady, currentMap, submitting]);

  // Next Map — the React side advances currentMap + resets startTime
  // (local display). The bridge-side mapStartedAtMs is reset by the
  // `psydechat:advance_map` envelope so the next Submit click uses
  // the freshly-started per-map time. The toolbar trusts its own
  // currentMap because the bridge
  // emit-on-advance_map path already updates both sides.)
  const advanceMap = useCallback(() => {
    if (!bundleReady) return;
    const next = nextMapId(currentMap);
    setCurrentMap(next);
    setStartTime(Date.now());
    api.emit({ type: 'psydechat:advance_map', nextMap: next });
  }, [api, bundleReady, currentMap]);

  // Restart Timer — for when the player wants to retry the current
  // map without advancing. Bridge-side mapStartedAtMs reset only;
  // currentMap stays put.
  const restartTimer = useCallback(() => {
    if (!bundleReady) return;
    setStartTime(Date.now());
    api.emit({ type: 'psydechat:reset_timer' });
  }, [api, bundleReady]);

  return (
    <div className="flex flex-col gap-2 px-6 py-3" role="region" aria-label="FreeDoom toolbar">
      {/* Error-priority order (top → bottom):
          1. RED  · Engine failed / bridge unreachable — either
                 `psydechat:ready` did not arrive within ~4s of mount
                 (CDN block, CSP, 404 on ./js-dos.js) or the bridge
                 posted `psydechat:error` with the precise cause.
                 Highest urgency because there's ZERO chance the
                 bundle mounts on its own — the user must intervene.
          2. AMBER · Bundle not vendored — bridge reachable but no
                 `.jsdos` bundle has been mounted yet. User can
                 recover with `npm run vendor:freedoom`.
          The pills never overlap in production (engineFailed
          short-circuits bundleReady) but a slow-CDN edge case could
          briefly post both. The `booting` pill was removed: the
          bridge always posts `psydechat:ready` after init, so the
          `engineReady === false` window is ~50ms and a flickering
          "Booting…" chip was worse than no chip at all. */}
      {engineFailed && (
        <div
          className="flex items-start gap-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300"
          role="status"
          aria-live="assertive"
        >
          <XCircle size={14} className="shrink-0 mt-0.5" />
          <div className="flex flex-col min-w-0">
            <span className="text-[11px] font-bold uppercase tracking-widest">
              {state.engineError === 'engine load failed'
                ? 'Engine failed to load'
                : 'Bridge unreachable'}
            </span>
            <span
              className="text-[10px] font-mono text-red-200/80 truncate"
              title={state.engineErrorMessage ?? undefined}
            >
              {state.engineErrorMessage ??
                'check console · js-dos CDN or CSP block'}
            </span>
          </div>
        </div>
      )}
      {!bundleReady && state.engineReady && !engineFailed && (
        <div
          className="flex items-start gap-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300"
          role="status"
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="flex flex-col min-w-0">
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Bundle not vendored
            </span>
            <span className="text-[10px] font-mono text-amber-200/80 truncate">
              run <code>npm run vendor:freedoom</code> · then refresh
            </span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/40 text-pink-400 text-[11px] font-mono font-black tracking-widest uppercase shrink-0"
            aria-label={`current freeDoom map: ${currentMap}`}
          >
            {currentMap}
          </div>
          <div
            className="text-white font-mono text-xl tabular-nums tracking-wider truncate"
            aria-live="off"
            title={`elapsed time on ${currentMap}`}
          >
            {fmtTime(elapsed)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={restartTimer}
            disabled={!bundleReady}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-[10px] font-bold uppercase tracking-widest hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/5"
          >
            Restart Timer
          </button>
          <button
            type="button"
            onClick={advanceMap}
            disabled={!bundleReady}
            className="px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-[10px] font-bold uppercase tracking-widest hover:bg-cyan-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-cyan-500/10"
          >
            Next Map
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !bundleReady}
            className="px-4 py-2 rounded-lg bg-pink-500/15 border border-pink-500/40 text-pink-400 text-[10px] font-black uppercase tracking-widest hover:bg-pink-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-busy={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Time'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default createIframeGame({
  id: 'freedoom',
  displayName: 'FreeDoom',
  shortDescription:
    'Classic single-player FPS · non-competitive voice activity (v1).',
  // v1.2: per-map speedrun via upsert_speedrun. The launcher wires a
  // custom toolbar (Submit Time / Next Map / Restart Timer) so the
  // React wrapper drives the bridge's per-map timer; the host listener
  // routes psydechat:map_complete envelopes to `public.upsert_speedrun`. The
  // `leaderboard: 'time_ms'` setting also flips the leaderboards panel
  // to surface freeDoom rows in the FreeDoom tab.
  leaderboard: 'time_ms',
  sameOrigin: true,
  // Explicit `?bundle=freedoom2` override — the bridge's
  // `mountFirstAvailableBundle()` already prefers freedoom2 over
  // freedoom1 + freedm, but tagging the URL param makes the user's
  // chosen phase obvious from the devtools + gives the bridge's
  // `readInitialBundleOverride()` an explicit hint to honour.
  url: '/freedoom/index.html?bundle=freedoom2',
  icon: Skull,
  attribution: {
    text:
      'WAD © 2001–2024 Contributors to the Freedoom project · BSD-3-Clause  ·  Engine: js-dos (DOSBox-WASM) · GPL-2.0',
    license: 'BSD-3-Clause',
    href: 'https://github.com/freedoom/freedoom',
  },
  toolbar: (api) => <FreeDoomToolbar api={api} />,
});
